import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { FormField } from "./BaseForm";
import { SMEPackageFormData, FormProps } from "@/types/forms";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ChevronRight, ChevronLeft, Building2, MapPin, FileText, Shield, CheckCircle2 } from "lucide-react";

type SMEPackageFormProps = FormProps<SMEPackageFormData>;

const SMEPackageForm = ({ onSubmit, initialData = {}, buttonText = "Get Quote" }: SMEPackageFormProps) => {
  const [currentStep, setCurrentStep] = useState(1);
  const totalSteps = 5;

  const [formData, setFormData] = useState<SMEPackageFormData>({
    segment: (initialData.segment as SMEPackageFormData["segment"]) || "office",
    natureOfBusiness: initialData.natureOfBusiness || "",
    companyName: initialData.companyName || "",
    commercialRegistryNo: initialData.commercialRegistryNo || "",
    startDate: initialData.startDate || "",
    address: initialData.address || "",
    poBox: initialData.poBox || "",
    area: initialData.area || "",
    emirate: initialData.emirate || "",
    country: initialData.country || "United Arab Emirates",
    taxRegistrationNumber: initialData.taxRegistrationNumber || "",
    vatLegalName: initialData.vatLegalName || "",
    vatAddress: initialData.vatAddress || "",
    buildingSumInsured: initialData.buildingSumInsured || "",
    contentsSumInsured: initialData.contentsSumInsured || "",
    equipmentSumInsured: initialData.equipmentSumInsured || "",
    portableEquipmentSumInsured: initialData.portableEquipmentSumInsured || "",
    biGrossProfit: initialData.biGrossProfit || "",
    lossOfRent: initialData.lossOfRent || "",
    increasedCostOfWorking: initialData.increasedCostOfWorking || "",
    publicLiabilityLimit: initialData.publicLiabilityLimit || "publi-public-liability-25m",
    annualWages: initialData.annualWages || "",
    riskAssessment: initialData.riskAssessment || "",
    existingInsurance: initialData.existingInsurance || "",
    representationsAccepted: initialData.representationsAccepted ?? false,
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  const validateStep = (step: number): boolean => {
    const newErrors: Record<string, string> = {};

    if (step === 1) {
      if (!formData.segment) newErrors.segment = "Please select your business type";
      if (!formData.natureOfBusiness) newErrors.natureOfBusiness = "Nature of business is required";
      if (!formData.companyName) newErrors.companyName = "Company name is required";
      if (!formData.commercialRegistryNo) newErrors.commercialRegistryNo = "Trade / commercial registry number is required";
      if (!formData.startDate) newErrors.startDate = "Policy start date is required";
    }

    if (step === 2) {
      if (!formData.address) newErrors.address = "Business address is required";
      if (!formData.area) newErrors.area = "Area / district is required";
      if (!formData.emirate) newErrors.emirate = "Emirate is required";
      if (!formData.country) newErrors.country = "Country is required";
    }

    if (step === 3) {
      if (!formData.taxRegistrationNumber) newErrors.taxRegistrationNumber = "Tax registration number is required";
      if (!formData.vatLegalName) newErrors.vatLegalName = "VAT legal name is required";
      if (!formData.vatAddress) newErrors.vatAddress = "VAT address is required";
    }

    if (step === 4) {
      if (!formData.publicLiabilityLimit) newErrors.publicLiabilityLimit = "Select a public liability limit";
      if (!formData.annualWages) newErrors.annualWages = "Annual wages are required";
      if (formData.segment === "office") {
        if (!formData.buildingSumInsured) newErrors.buildingSumInsured = "Building sum insured is required for office";
        if (!formData.contentsSumInsured) newErrors.contentsSumInsured = "Contents sum insured is required for office";
      }
    }

    if (step === 5) {
      if (!formData.riskAssessment) newErrors.riskAssessment = "Please confirm if a risk assessment has been done";
      if (!formData.existingInsurance) newErrors.existingInsurance = "Please confirm if you have existing insurance";
      if (!formData.representationsAccepted) newErrors.representationsAccepted = "You must confirm the representations to continue";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (validateStep(currentStep)) {
      if (currentStep < totalSteps) {
        setCurrentStep(currentStep + 1);
        window.scrollTo({ top: 0, behavior: 'smooth' });
      } else {
        onSubmit(formData);
      }
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const updateField = <K extends keyof SMEPackageFormData>(field: K, value: SMEPackageFormData[K]) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: "" }));
    }
  };

  const isOffice = formData.segment === "office";

  const stepConfig = [
    { number: 1, title: "Business Profile", icon: Building2 },
    { number: 2, title: "Location", icon: MapPin },
    { number: 3, title: "Tax & VAT", icon: FileText },
    { number: 4, title: "Coverage", icon: Shield },
    { number: 5, title: "Confirmation", icon: CheckCircle2 },
  ];

  return (
    <div className="space-y-6">
      {/* Progress indicator */}
      <div className="flex items-center justify-center gap-2 mb-8">
        {stepConfig.map((step, idx) => {
          const Icon = step.icon;
          const isActive = currentStep === step.number;
          const isComplete = currentStep > step.number;
          return (
            <div key={step.number} className="flex items-center">
              <div className={`flex flex-col items-center ${isActive ? 'scale-110' : 'scale-100'} transition-transform duration-200`}>
                <div
                  className={`w-10 h-10 rounded-full flex items-center justify-center transition-all duration-300 ${
                    isComplete
                      ? 'bg-green-500 text-white'
                      : isActive
                      ? 'bg-blue-600 text-white shadow-lg'
                      : 'bg-gray-200 text-gray-400'
                  }`}
                >
                  {isComplete ? <CheckCircle2 className="w-5 h-5" /> : <Icon className="w-5 h-5" />}
                </div>
                <span className={`text-xs mt-1 font-medium ${isActive ? 'text-blue-600' : 'text-gray-500'}`}>
                  {step.title}
                </span>
              </div>
              {idx < stepConfig.length - 1 && (
                <div className={`w-8 h-0.5 mx-1 mb-5 transition-colors duration-300 ${isComplete ? 'bg-green-500' : 'bg-gray-200'}`} />
              )}
            </div>
          );
        })}
      </div>

      {/* Step content */}
      <div className="min-h-[400px]">
        {currentStep === 1 && (
          <div className="space-y-6 animate-in fade-in slide-in-from-right-4 duration-300">
            <div className="text-center mb-6">
              <h2 className="text-2xl font-bold text-gray-900">Tell us about your business</h2>
              <p className="text-gray-600 mt-2">We'll use this to tailor your coverage</p>
            </div>
            <div className="space-y-4">
              <FormField label="Business Type" required>
                <p className="text-sm text-gray-500 mb-2">What type of business do you run? This helps us understand what kind of protection you need.</p>
                <Select
                  value={formData.segment}
                  onValueChange={(value) => updateField("segment", value as SMEPackageFormData["segment"])}
                >
                  <SelectTrigger className="h-12">
                    <SelectValue placeholder="Select business type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="office">🏢 Office</SelectItem>
                    <SelectItem value="retail">🛍️ Retail</SelectItem>
                    <SelectItem value="fnb">🍽️ Food &amp; Beverage</SelectItem>
                  </SelectContent>
                </Select>
                {errors.segment && <p className="text-red-500 text-sm mt-1">{errors.segment}</p>}
              </FormField>

              <FormField label="Nature of Business" required>
                <p className="text-sm text-gray-500 mb-2">What does your business do? (e.g., "Accounting firm" or "Coffee shop")</p>
                <Input
                  className="h-12"
                  placeholder="e.g. Architectural services, consulting"
                  value={formData.natureOfBusiness}
                  onChange={(e) => updateField("natureOfBusiness", e.target.value)}
                />
                {errors.natureOfBusiness && <p className="text-red-500 text-sm mt-1">{errors.natureOfBusiness}</p>}
              </FormField>

              <FormField label="Company Name" required>
                <p className="text-sm text-gray-500 mb-2">The official registered name of your company</p>
                <Input
                  className="h-12"
                  placeholder="Registered company name"
                  value={formData.companyName}
                  onChange={(e) => updateField("companyName", e.target.value)}
                />
                {errors.companyName && <p className="text-red-500 text-sm mt-1">{errors.companyName}</p>}
              </FormField>

              <FormField label="Trade Licence Number" required>
                <p className="text-sm text-gray-500 mb-2">Your business licence number (found on your trade licence document)</p>
                <Input
                  className="h-12"
                  placeholder="Trade licence / commercial registry number"
                  value={formData.commercialRegistryNo}
                  onChange={(e) => updateField("commercialRegistryNo", e.target.value)}
                />
                {errors.commercialRegistryNo && <p className="text-red-500 text-sm mt-1">{errors.commercialRegistryNo}</p>}
              </FormField>

              <FormField label="When do you want coverage to start?" required>
                <p className="text-sm text-gray-500 mb-2">The date you want your insurance to begin</p>
                <Input
                  className="h-12"
                  type="date"
                  value={formData.startDate}
                  onChange={(e) => updateField("startDate", e.target.value)}
                />
                {errors.startDate && <p className="text-red-500 text-sm mt-1">{errors.startDate}</p>}
              </FormField>
            </div>
          </div>
        )}

        {currentStep === 2 && (
          <div className="space-y-6 animate-in fade-in slide-in-from-right-4 duration-300">
            <div className="text-center mb-6">
              <h2 className="text-2xl font-bold text-gray-900">Where is your business located?</h2>
              <p className="text-gray-600 mt-2">We need your business address for the policy</p>
            </div>
            <div className="space-y-4">
              <FormField label="Address" required>
                <p className="text-sm text-gray-500 mb-2">Your business street address (building name, street, etc.)</p>
                <Input
                  className="h-12"
                  placeholder="Building, street, area"
                  value={formData.address}
                  onChange={(e) => updateField("address", e.target.value)}
                />
                {errors.address && <p className="text-red-500 text-sm mt-1">{errors.address}</p>}
              </FormField>

              <FormField label="PO Box">
                <p className="text-sm text-gray-500 mb-2">Your postal box number (optional)</p>
                <Input
                  className="h-12"
                  placeholder="PO Box (optional)"
                  value={formData.poBox}
                  onChange={(e) => updateField("poBox", e.target.value)}
                />
                {errors.poBox && <p className="text-red-500 text-sm mt-1">{errors.poBox}</p>}
              </FormField>

              <FormField label="Area / District" required>
                <p className="text-sm text-gray-500 mb-2">The neighborhood or district (e.g., "JLT" or "Business Bay")</p>
                <Input
                  className="h-12"
                  placeholder="e.g. JLT, Business Bay"
                  value={formData.area}
                  onChange={(e) => updateField("area", e.target.value)}
                />
                {errors.area && <p className="text-red-500 text-sm mt-1">{errors.area}</p>}
              </FormField>

              <FormField label="Emirate" required>
                <p className="text-sm text-gray-500 mb-2">Which emirate is your business in?</p>
                <Select
                  value={formData.emirate}
                  onValueChange={(value) => updateField("emirate", value)}
                >
                  <SelectTrigger className="h-12">
                    <SelectValue placeholder="Select emirate" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Abu Dhabi">Abu Dhabi</SelectItem>
                    <SelectItem value="Dubai">Dubai</SelectItem>
                    <SelectItem value="Sharjah">Sharjah</SelectItem>
                    <SelectItem value="Ajman">Ajman</SelectItem>
                    <SelectItem value="Umm Al Quwain">Umm Al Quwain</SelectItem>
                    <SelectItem value="Ras Al Khaimah">Ras Al Khaimah</SelectItem>
                    <SelectItem value="Fujairah">Fujairah</SelectItem>
                  </SelectContent>
                </Select>
                {errors.emirate && <p className="text-red-500 text-sm mt-1">{errors.emirate}</p>}
              </FormField>

              <FormField label="Country" required>
                <p className="text-sm text-gray-500 mb-2">The country where your business operates</p>
                <Select
                  value={formData.country}
                  onValueChange={(value) => updateField("country", value)}
                >
                  <SelectTrigger className="h-12">
                    <SelectValue placeholder="Select country" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="United Arab Emirates">🇦🇪 United Arab Emirates</SelectItem>
                  </SelectContent>
                </Select>
                {errors.country && <p className="text-red-500 text-sm mt-1">{errors.country}</p>}
              </FormField>
            </div>
          </div>
        )}

        {currentStep === 3 && (
          <div className="space-y-6 animate-in fade-in slide-in-from-right-4 duration-300">
            <div className="text-center mb-6">
              <h2 className="text-2xl font-bold text-gray-900">Tax & VAT information</h2>
              <p className="text-gray-600 mt-2">We need your tax details for the insurance policy</p>
            </div>
            <div className="space-y-4">
              <FormField label="Tax Registration Number (TRN)" required>
                <p className="text-sm text-gray-500 mb-2">Your 15-digit tax number from the Federal Tax Authority (starts with "1")</p>
                <Input
                  className="h-12"
                  placeholder="e.g. 123456789012345"
                  value={formData.taxRegistrationNumber}
                  onChange={(e) => updateField("taxRegistrationNumber", e.target.value)}
                />
                {errors.taxRegistrationNumber && <p className="text-red-500 text-sm mt-1">{errors.taxRegistrationNumber}</p>}
              </FormField>

              <FormField label="VAT Legal Name" required>
                <p className="text-sm text-gray-500 mb-2">The exact company name on your VAT certificate</p>
                <Input
                  className="h-12"
                  placeholder="Legal name for VAT"
                  value={formData.vatLegalName}
                  onChange={(e) => updateField("vatLegalName", e.target.value)}
                />
                {errors.vatLegalName && <p className="text-red-500 text-sm mt-1">{errors.vatLegalName}</p>}
              </FormField>

              <FormField label="VAT Address" required>
                <p className="text-sm text-gray-500 mb-2">The address shown on your VAT registration</p>
                <Input
                  className="h-12"
                  placeholder="Address used for VAT registration"
                  value={formData.vatAddress}
                  onChange={(e) => updateField("vatAddress", e.target.value)}
                />
                {errors.vatAddress && <p className="text-red-500 text-sm mt-1">{errors.vatAddress}</p>}
              </FormField>
            </div>
          </div>
        )}

        {currentStep === 4 && (
          <div className="space-y-6 animate-in fade-in slide-in-from-right-4 duration-300">
            <div className="text-center mb-6">
              <h2 className="text-2xl font-bold text-gray-900">Coverage & protection</h2>
              <p className="text-gray-600 mt-2">Choose the right coverage for your business</p>
            </div>
            <div className="space-y-4">
              {isOffice && (
                <>
                  <FormField label="Building sum insured (AED)" required>
                    <p className="text-sm text-gray-500 mb-2">How much would it cost to rebuild your office if it was destroyed? (e.g., walls, floors, fixtures)</p>
                    <Input
                      className="h-12"
                      type="number"
                      min="0"
                      placeholder="e.g. 1,000,000"
                      value={formData.buildingSumInsured}
                      onChange={(e) => updateField("buildingSumInsured", e.target.value)}
                    />
                    {errors.buildingSumInsured && <p className="text-red-500 text-sm mt-1">{errors.buildingSumInsured}</p>}
                  </FormField>

                  <FormField label="Contents sum insured (AED)" required>
                    <p className="text-sm text-gray-500 mb-2">What's the total value of your furniture, computers, and office stuff? (desks, chairs, laptops, printers, etc.)</p>
                    <Input
                      className="h-12"
                      type="number"
                      min="0"
                      placeholder="e.g. 200,000"
                      value={formData.contentsSumInsured}
                      onChange={(e) => updateField("contentsSumInsured", e.target.value)}
                    />
                    {errors.contentsSumInsured && <p className="text-red-500 text-sm mt-1">{errors.contentsSumInsured}</p>}
                  </FormField>

                  <FormField label="Equipment sum insured (AED)">
                    <p className="text-sm text-gray-500 mb-2">Value of specialized equipment (servers, machinery, tools). Leave blank if you don't have any.</p>
                    <Input
                      className="h-12"
                      type="number"
                      min="0"
                      placeholder="e.g. 300,000 (optional)"
                      value={formData.equipmentSumInsured}
                      onChange={(e) => updateField("equipmentSumInsured", e.target.value)}
                    />
                    {errors.equipmentSumInsured && <p className="text-red-500 text-sm mt-1">{errors.equipmentSumInsured}</p>}
                  </FormField>

                  <FormField label="Portable equipment sum insured (AED)">
                    <p className="text-sm text-gray-500 mb-2">Value of items you take outside the office (laptops, tablets, cameras, phones). Leave blank if none.</p>
                    <Input
                      className="h-12"
                      type="number"
                      min="0"
                      placeholder="e.g. 100,000 (optional)"
                      value={formData.portableEquipmentSumInsured}
                      onChange={(e) => updateField("portableEquipmentSumInsured", e.target.value)}
                    />
                    {errors.portableEquipmentSumInsured && <p className="text-red-500 text-sm mt-1">{errors.portableEquipmentSumInsured}</p>}
                  </FormField>

                  <FormField label="Business interruption gross profit (AED)">
                    <p className="text-sm text-gray-500 mb-2">How much profit would you lose if your business had to close for a few months? (covers lost income + ongoing costs)</p>
                    <Input
                      className="h-12"
                      type="number"
                      min="0"
                      placeholder="e.g. 500,000 (optional)"
                      value={formData.biGrossProfit}
                      onChange={(e) => updateField("biGrossProfit", e.target.value)}
                    />
                    {errors.biGrossProfit && <p className="text-red-500 text-sm mt-1">{errors.biGrossProfit}</p>}
                  </FormField>

                  <FormField label="Loss of rent (annual rent, AED)">
                    <p className="text-sm text-gray-500 mb-2">If you rent your office, how much rent do you pay per year? (we'll cover it if the building becomes unusable)</p>
                    <Input
                      className="h-12"
                      type="number"
                      min="0"
                      placeholder="e.g. 200,000 (optional)"
                      value={formData.lossOfRent}
                      onChange={(e) => updateField("lossOfRent", e.target.value)}
                    />
                    {errors.lossOfRent && <p className="text-red-500 text-sm mt-1">{errors.lossOfRent}</p>}
                  </FormField>

                  <FormField label="Increased cost of working (AED)">
                    <p className="text-sm text-gray-500 mb-2">Extra costs to keep running if disaster strikes (e.g., renting temporary office, overtime pay). Leave blank if unsure.</p>
                    <Input
                      className="h-12"
                      type="number"
                      min="0"
                      placeholder="e.g. 100,000 (optional)"
                      value={formData.increasedCostOfWorking}
                      onChange={(e) => updateField("increasedCostOfWorking", e.target.value)}
                    />
                    {errors.increasedCostOfWorking && <p className="text-red-500 text-sm mt-1">{errors.increasedCostOfWorking}</p>}
                  </FormField>
                </>
              )}

              <FormField label="Public liability limit" required>
                <p className="text-sm text-gray-500 mb-2">How much coverage do you want if someone gets hurt or their property is damaged because of your business? (higher = more protection)</p>
                <Select
                  value={formData.publicLiabilityLimit}
                  onValueChange={(value) => updateField("publicLiabilityLimit", value)}
                >
                  <SelectTrigger className="h-12">
                    <SelectValue placeholder="Select limit" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="publi-public-liability-25m">AED 2.5 million</SelectItem>
                    <SelectItem value="publi-public-liability-5m">AED 5 million</SelectItem>
                    <SelectItem value="publi-public-liability-10m">AED 10 million</SelectItem>
                  </SelectContent>
                </Select>
                {errors.publicLiabilityLimit && <p className="text-red-500 text-sm mt-1">{errors.publicLiabilityLimit}</p>}
              </FormField>

              <FormField label="Annual wages of employees (AED)" required>
                <p className="text-sm text-gray-500 mb-2">Total salaries you pay all your employees in one year (needed for workers' compensation insurance)</p>
                <Input
                  className="h-12"
                  type="number"
                  min="0"
                  placeholder="Total annual wages"
                  value={formData.annualWages}
                  onChange={(e) => updateField("annualWages", e.target.value)}
                />
                {errors.annualWages && <p className="text-red-500 text-sm mt-1">{errors.annualWages}</p>}
              </FormField>
            </div>
          </div>
        )}

        {currentStep === 5 && (
          <div className="space-y-6 animate-in fade-in slide-in-from-right-4 duration-300">
            <div className="text-center mb-6">
              <h2 className="text-2xl font-bold text-gray-900">Final confirmation</h2>
              <p className="text-gray-600 mt-2">Just a few more details before we get your quote</p>
            </div>
            <div className="space-y-4">
              <FormField label="Has a risk assessment been done?" required>
                <p className="text-sm text-gray-500 mb-2">Have you checked your business for safety hazards? (e.g., fire risks, trip hazards, security issues)</p>
                <Select
                  value={formData.riskAssessment}
                  onValueChange={(value) => updateField("riskAssessment", value as SMEPackageFormData["riskAssessment"])}
                >
                  <SelectTrigger className="h-12">
                    <SelectValue placeholder="Select" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="yes">✅ Yes</SelectItem>
                    <SelectItem value="no">❌ No</SelectItem>
                  </SelectContent>
                </Select>
                {errors.riskAssessment && <p className="text-red-500 text-sm mt-1">{errors.riskAssessment}</p>}
              </FormField>

              <FormField label="Do you currently have existing insurance?" required>
                <p className="text-sm text-gray-500 mb-2">Do you already have business insurance with another company?</p>
                <Select
                  value={formData.existingInsurance}
                  onValueChange={(value) => updateField("existingInsurance", value as SMEPackageFormData["existingInsurance"])}
                >
                  <SelectTrigger className="h-12">
                    <SelectValue placeholder="Select" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="yes">✅ Yes</SelectItem>
                    <SelectItem value="no">❌ No</SelectItem>
                  </SelectContent>
                </Select>
                {errors.existingInsurance && <p className="text-red-500 text-sm mt-1">{errors.existingInsurance}</p>}
              </FormField>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mt-6">
                <FormField label="">
                  <div className="flex items-start space-x-3">
                    <input
                      id="representationsAccepted"
                      type="checkbox"
                      className="h-5 w-5 mt-0.5 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      checked={formData.representationsAccepted}
                      onChange={(e) => updateField("representationsAccepted", e.target.checked)}
                    />
                    <label htmlFor="representationsAccepted" className="text-sm text-gray-700 leading-relaxed">
                      I confirm that all information provided is accurate and complete. I am authorised to request this quote on behalf of the business.
                    </label>
                  </div>
                  {errors.representationsAccepted && <p className="text-red-500 text-sm mt-2">{errors.representationsAccepted}</p>}
                </FormField>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Navigation buttons */}
      <div className="flex items-center justify-between gap-4 pt-6 border-t">
        {currentStep > 1 ? (
          <Button
            type="button"
            variant="outline"
            onClick={handleBack}
            className="flex items-center gap-2"
          >
            <ChevronLeft className="w-4 h-4" />
            Back
          </Button>
        ) : (
          <div />
        )}
        <Button
          type="button"
          onClick={handleNext}
          className="flex items-center gap-2 ml-auto px-8"
        >
          {currentStep === totalSteps ? buttonText : 'Continue'}
          {currentStep < totalSteps && <ChevronRight className="w-4 h-4" />}
        </Button>
      </div>
    </div>
  );
};

export default SMEPackageForm;

